import { useState, useEffect } from "react";
import { api, mediaUrl } from "../lib/api";
import { Sparkles, Loader2, X } from "lucide-react";
import { sanitizeHtml } from "../lib/sanitize";

/**
 * AI Assist modal for the article editor.
 * Modes:
 *  - "outline": generate an article outline from a topic + optional notes
 *  - "rephrase": rephrase a passage in a chosen tone
 *  - "cover": generate a cover image via Nano Banana
 *
 * Props:
 *   open, onClose, mode, defaultText (passage for rephrase)
 *   onApplyText(html)   — invoked when user accepts text (outline/rephrase)
 *   onApplyCover(media) — invoked when user accepts cover image (full media record)
 */
export function AIAssistDialog({ open, onClose, mode, defaultText = "", onApplyText, onApplyCover, articleTitle = "", articleCategory = "" }) {
  const [topic, setTopic] = useState(articleTitle);
  const [notes, setNotes] = useState("");
  const [tone, setTone] = useState("editorial");
  const [text, setText] = useState(defaultText);
  const [prompt, setPrompt] = useState(articleTitle);
  const [style, setStyle] = useState("editorial");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // {html} or {text} or {media}

  // Re-sync local state from props each time the dialog re-opens
  // (props may have changed while it was closed — e.g. user typed in title field).
  useEffect(() => {
    if (open) {
      setTopic(articleTitle);
      setPrompt(articleTitle);
      setText(defaultText);
      setResult(null);
      setError("");
    }
  }, [open, articleTitle, defaultText]);

  if (!open) return null;

  const reset = () => { setResult(null); setError(""); };

  const run = async () => {
    setLoading(true); setError(""); setResult(null);
    try {
      if (mode === "outline") {
        const r = await api.post("/admin/ai/outline", { topic, category: articleCategory, notes });
        setResult({ kind: "html", value: r.data.html });
      } else if (mode === "rephrase") {
        const r = await api.post("/admin/ai/rephrase", { text, tone });
        setResult({ kind: "text", value: r.data.text });
      } else if (mode === "cover") {
        const r = await api.post("/admin/ai/cover", { prompt, style });
        setResult({ kind: "cover", value: r.data });
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "AI request failed");
    } finally { setLoading(false); }
  };

  const apply = () => {
    if (!result) return;
    if (result.kind === "html" || result.kind === "text") onApplyText?.(result.value);
    if (result.kind === "cover") onApplyCover?.(result.value);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-aubergine/40 backdrop-blur-sm" data-testid="ai-assist-dialog">
      <div className="w-full max-w-2xl rounded-lg bg-white border border-aubergine/15 shadow-xl overflow-hidden">
        <header className="flex items-center justify-between px-5 py-3 border-b border-aubergine/10 bg-bone/60">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-champagne" />
            <h3 className="font-serif text-lg text-aubergine">
              {mode === "outline" && <>AI <em className="accent-italic">outline</em></>}
              {mode === "rephrase" && <>AI <em className="accent-italic">rephrase</em></>}
              {mode === "cover" && <>AI cover <em className="accent-italic">image</em></>}
            </h3>
          </div>
          <button onClick={onClose} data-testid="ai-assist-close" className="p-1 rounded hover:bg-aubergine/5 text-aubergine/55"><X className="h-4 w-4" /></button>
        </header>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {!result && (
            <>
              {mode === "outline" && (
                <>
                  <Field label="Topic">
                    <input data-testid="ai-outline-topic" value={topic} onChange={(e) => setTopic(e.target.value)} className={inputCls} placeholder="What is the article about?" />
                  </Field>
                  <Field label="Additional notes" hint="Optional — angle, audience, key points to emphasize.">
                    <textarea data-testid="ai-outline-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className={inputCls + " resize-none"} />
                  </Field>
                  <p className="text-[11px] text-aubergine/55">Category: <span className="font-mono uppercase tracking-wider">{articleCategory || "—"}</span></p>
                </>
              )}
              {mode === "rephrase" && (
                <>
                  <Field label="Tone">
                    <select data-testid="ai-rephrase-tone" value={tone} onChange={(e) => setTone(e.target.value)} className={inputCls}>
                      <option value="editorial">Editorial (FT / BIS)</option>
                      <option value="punchy">Punchy</option>
                      <option value="technical">More technical</option>
                      <option value="simpler">Simpler / non-expert</option>
                    </select>
                  </Field>
                  <Field label="Passage to rephrase">
                    <textarea data-testid="ai-rephrase-text" value={text} onChange={(e) => setText(e.target.value)} rows={7} className={inputCls + " resize-none font-mono text-xs"} />
                  </Field>
                </>
              )}
              {mode === "cover" && (
                <>
                  <Field label="Cover image prompt">
                    <textarea data-testid="ai-cover-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} className={inputCls + " resize-none"} placeholder="e.g. Marble columns of an institutional building at dusk, soft warm light" />
                  </Field>
                  <Field label="Style">
                    <select data-testid="ai-cover-style" value={style} onChange={(e) => setStyle(e.target.value)} className={inputCls}>
                      <option value="editorial">Editorial photography</option>
                      <option value="abstract">Abstract architectural</option>
                      <option value="diagrammatic">Diagrammatic line-art</option>
                    </select>
                  </Field>
                  <p className="text-[11px] text-aubergine/55">Generation may take 10–20 seconds.</p>
                </>
              )}
            </>
          )}

          {error && <div className="rounded-md border border-signal-red/40 bg-signal-red/10 px-4 py-2 text-sm text-signal-red" data-testid="ai-assist-error">{error}</div>}

          {result?.kind === "html" && (
            <div className="rounded-md border border-aubergine/10 bg-bone/40 p-4 prose-trex" style={{ color: "var(--aubergine)" }} data-testid="ai-result-html">
              <div className="text-aubergine [&>p]:text-aubergine/85 [&>h2]:text-aubergine [&>em]:text-champagne" dangerouslySetInnerHTML={{ __html: sanitizeHtml(result.value) }} />
            </div>
          )}
          {result?.kind === "text" && (
            <div className="rounded-md border border-aubergine/10 bg-bone/40 p-4 text-sm text-aubergine whitespace-pre-line" data-testid="ai-result-text">
              {result.value}
            </div>
          )}
          {result?.kind === "cover" && (
            <div className="rounded-md overflow-hidden border border-aubergine/10" data-testid="ai-result-cover">
              <img
                src={mediaUrl(result.value.media_id)}
                alt="AI cover"
                className="w-full h-auto"
              />
            </div>
          )}
        </div>

        <footer className="flex flex-wrap items-center justify-end gap-2 px-5 py-3 border-t border-aubergine/10 bg-bone/40">
          {result ? (
            <>
              <button onClick={reset} data-testid="ai-assist-retry" className="rounded-md border border-aubergine/20 hover:bg-aubergine/5 px-3 py-2 font-mono text-xs text-aubergine">Try again</button>
              <button onClick={apply} data-testid="ai-assist-apply" className="rounded-md bg-aubergine text-bone hover:bg-aubergine/90 px-4 py-2 font-mono text-xs">
                {mode === "cover" ? "Use as cover" : "Insert"}
              </button>
            </>
          ) : (
            <>
              <button onClick={onClose} data-testid="ai-assist-cancel" className="rounded-md border border-aubergine/20 hover:bg-aubergine/5 px-3 py-2 font-mono text-xs text-aubergine">Cancel</button>
              <button onClick={run} disabled={loading} data-testid="ai-assist-run" className="inline-flex items-center gap-2 rounded-md bg-aubergine text-bone hover:bg-aubergine/90 px-4 py-2 font-mono text-xs disabled:opacity-60">
                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                Generate
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-md border border-aubergine/15 bg-white px-3 py-2 text-sm outline-none focus:border-champagne transition-colors";

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-aubergine mb-1.5">{label}</label>
      {children}
      {hint && <p className="mt-1 text-[11px] text-aubergine/55">{hint}</p>}
    </div>
  );
}
